import React from 'react';
import { useSelector } from 'react-redux';

import { Attention } from '@travel/icons/ui';
import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import { GalleryIcon } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import DataLayer from 'components/DataLayer';
import Media from 'components/Media';
import PhotoGallery from 'components/PhotoGallery';
import Swiper from 'components/Swiper';
import { BookmarkButton } from 'pages/ProviderListPage/components/ProviderBox/ProviderHeaderBox';

import { getDetailSummary, getGalleryCoverInformation } from 'store/providerInformation/selectors';

import { GALLERY_INDEX_QUERY, GALLERY_QUERY, GALLERY_QUERY_VALUE } from 'constants/gallery';
import { Translate } from 'core/translate';
import useQueryState from 'hooks/useQueryState';
import useQueryToggle from 'hooks/useQueryToggle';

import DetailSummary from '../DetailSummary';
import ThumbnailList from '../ThumbnailList';

import styles from './providerInformationHeader.module.scss';

type ProviderInformationHeaderProps = {
  /** on what parent page this component lives */
  onPage: 'providerInfoUndated' | 'providerInfoDated';
  className?: string;
  /** String to define the current provider id */
  pageIdentifier: string;
  /** RAT page name  */
  pageName: 'hotel:rooms_and_plans_dated_list' | 'hotel:rooms_and_plans_undated_list';
};

function ProviderInformationHeader(props: ProviderInformationHeaderProps) {
  const { pageIdentifier, className, pageName, onPage } = props;

  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;

  const {
    isOpen: isOpenGallery,
    onClose: onCloseGallery,
    onOpen: onOpenGallery,
  } = useQueryToggle(GALLERY_QUERY, GALLERY_QUERY_VALUE.Thumbnail);
  const [initImageIndex, setInitImageIndex] = useQueryState(GALLERY_INDEX_QUERY, {
    defaultValue: 0,
    parser: parseInt,
  });

  const handleOnOpenGallery = (index: number) => () => {
    onOpenGallery();
    setInitImageIndex(index);
  };

  const detailSummary = useSelector(getDetailSummary(pageIdentifier));
  const galleryCoverInformation = useSelector(getGalleryCoverInformation);

  const inActiveLabel = detailSummary.isActive === false && (
    <div className={styles.inactiveLabel}>
      <Attention className={styles.icon} size={16} />
      <Translate id="Provider_Info.Provider_Details.Inactive" />
    </div>
  );

  return (
    <div className={cx(styles.wrapper, className)} data-testid="providerInformationHeader-wrapper">
      {!isMobile && inActiveLabel}
      {isMobile && (
        <div className={styles.container}>
          <div className={styles.galleryCoverWrapper}>
            {isNotEmptyArray(detailSummary.galleryData) ? (
              <>
                {detailSummary.photosCount === 1 ? (
                  <div className={styles.galleryDetails}>
                    <Media
                      key={detailSummary.galleryData[0].url}
                      wrapperClassName={styles.mediaWrapper}
                      media={detailSummary.galleryData[0]}
                      sizes="100vw"
                      onClick={onOpenGallery}
                      isDisableLazyload={true}
                    />
                  </div>
                ) : (
                  <Swiper
                    className={styles.galleryDetails}
                    paginationType="swipe"
                    shouldRenderAllChild={false}
                  >
                    {detailSummary.galleryData.map((media, index) => (
                      <Media
                        key={`detail-media-${media.url}`}
                        wrapperClassName={styles.mediaWrapper}
                        media={media}
                        sizes="100vw"
                        onClick={handleOnOpenGallery(index)}
                        isDisableLazyload={index === 0}
                      />
                    ))}
                  </Swiper>
                )}
                <GalleryIcon
                  photosCount={detailSummary.photosCount}
                  className={styles.galleryIcon}
                  onClick={onOpenGallery}
                />
              </>
            ) : (
              <div className={styles.galleryDetails}>
                <Media sizes="100vw" isMediaOnly={true} />
              </div>
            )}
          </div>
          {inActiveLabel}
        </div>
      )}

      {!isMobile && (
        <ThumbnailList
          onPage={onPage}
          media={detailSummary.galleryData}
          isDisplaySeeAllPhotos={true}
          onClick={handleOnOpenGallery}
        />
      )}

      {isMobile && (
        <DetailSummary
          pageName={pageName}
          pageIdentifier={pageIdentifier}
          isShare={galleryCoverInformation.isShare}
        />
      )}

      <PhotoGallery
        galleryData={detailSummary.galleryData}
        isOpenPhotoGallery={isOpenGallery}
        onClosePhotoGallery={onCloseGallery}
        initImageIndex={initImageIndex}
        bookmarkButton={
          <div className={styles.bookmarkWrapper}>
            <BookmarkButton
              className={styles.bookmarkButton}
              providerId={detailSummary.id}
              isBookmarked={Boolean(detailSummary.isBookmarked)}
              iconStyle="gold"
              iconSize={24}
            />
            <Translate
              id={
                detailSummary.isBookmarked
                  ? 'Provider_Info.Gallery.Remove_Bookmark'
                  : 'Provider_Info.Gallery.Bookmark'
              }
              className={styles.bookmarkText}
            />
          </div>
        }
      />

      {isOpenGallery && (
        <DataLayer
          pageName={`${pageName}:hotel_photo_gallery_popup`}
          pageType="shop_item"
          siteSection="hotel"
        />
      )}
    </div>
  );
}

export default React.memo(ProviderInformationHeader);
